if (!customElements.get('collection-products')) {
  customElements.define(
    'collection-products',
    class CollectionProducts extends HTMLElement {
      constructor() {
        super();
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onPopState = this.onPopState.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('[data-collection-form]');
        this.panel = this.querySelector('[data-filters-panel]');
        this.openButton = this.querySelector('[data-filters-open]');

        this.form?.addEventListener('change', this.onChange);
        this.form?.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
        this.addEventListener('toggle', this.onToggle, true);
        this.addEventListener('keyup', this.onKeyup);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('popstate', this.onPopState);
        document.body.style.removeProperty('overflow');
      }

      onChange(event) {
        clearTimeout(this.changeTimer);
        const delay = event.target.type === 'number' ? 600 : 0;
        this.changeTimer = setTimeout(() => this.renderFromForm(), delay);
      }

      onSubmit(event) {
        event.preventDefault();
        this.renderFromForm();
        this.closeDrawer();
      }

      onClick(event) {
        const link = event.target.closest('[data-collection-link]');
        if (link) {
          event.preventDefault();
          this.render(link.href, { scroll: link.hasAttribute('data-collection-scroll') });
          return;
        }

        if (event.target.closest('[data-filters-open]')) this.openDrawer();
        if (event.target.closest('[data-filters-close]')) this.closeDrawer();
      }

      onToggle(event) {
        const details = event.target;
        if (!details.matches?.('[data-filter]') || !details.open || this.mobileQuery.matches) return;

        this.querySelectorAll('[data-filter][open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      }

      onKeyup(event) {
        if (event.key !== 'Escape') return;

        if (this.panel?.classList.contains('collection-filters--open')) {
          this.closeDrawer();
          return;
        }

        const openFilter = event.target.closest('[data-filter][open]');
        if (openFilter) {
          openFilter.open = false;
          openFilter.querySelector('summary').focus();
        }
      }

      onDocumentClick(event) {
        if (this.mobileQuery.matches) return;

        this.querySelectorAll('[data-filter][open]').forEach((details) => {
          if (!details.contains(event.target)) details.open = false;
        });
      }

      onPopState() {
        if (window.location.pathname === this.dataset.collectionPath) {
          this.render(window.location.href, { push: false });
        } else if (!document.querySelector('collection-links')) {
          window.location.reload();
        }
      }

      openDrawer() {
        if (!this.panel) return;

        this.panel.querySelectorAll('[data-filter]').forEach((details) => (details.open = true));
        this.panel.classList.add('collection-filters--open');
        this.openButton.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.panel.querySelector('[data-filters-close]').focus();
      }

      closeDrawer() {
        if (!this.panel?.classList.contains('collection-filters--open')) return;

        this.panel.classList.remove('collection-filters--open');
        this.panel.querySelectorAll('[data-filter]').forEach((details) => (details.open = false));
        this.openButton.setAttribute('aria-expanded', 'false');
        document.body.style.removeProperty('overflow');
        this.openButton.focus();
      }

      renderFromForm() {
        const params = new URLSearchParams();
        for (const [key, value] of new FormData(this.form)) {
          if (value !== '') params.append(key, value);
        }

        this.render(`${this.form.action}?${params}`);
      }

      async render(url, { push = true, scroll = false } = {}) {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', this.dataset.sectionId);

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(target, { signal: this.abortController.signal });
          const html = await response.text();
          this.update(new DOMParser().parseFromString(html, 'text/html'));

          target.searchParams.delete('section_id');
          if (push) window.history.pushState({}, '', target);
          if (scroll) this.scrollToTop();
        } catch (error) {
          if (error.name !== 'AbortError') window.location.assign(url);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      scrollToTop() {
        const anchor = document.querySelector('.collection-header') || this;
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
        const anchorTop = anchor.getBoundingClientRect().top;
        if (anchorTop >= headerHeight && anchorTop < window.innerHeight / 2) return;

        window.scrollTo({ top: Math.max(anchorTop + window.scrollY - headerHeight - 12, 0), behavior: 'smooth' });
      }

      update(page) {
        const focused = document.activeElement;
        const typing = focused?.matches('input[type="number"], input[type="text"]');

        this.querySelectorAll('[data-collection-update]').forEach((element) => {
          const key = element.dataset.collectionUpdate;
          const fresh = page.querySelector(`[data-collection-update="${key}"]`);
          if (!fresh || (typing && element.contains(focused))) return;

          element.innerHTML = fresh.innerHTML;
        });
      }
    }
  );
}
